import React, { useState } from 'react';
import EventRow from './EventRow';
import LiquidFillLoader from '../../../shared/components/ui/LiquidFillLoader';
import { usePreferences } from '../../../shared/context/preferencesContext';
import { AVAILABLE_COLUMNS } from '../config/eventColumns';
import { TABLE_STYLES } from '../styles/dataTableStyles';

const EventsTable = ({ events, loading, loadingMore, error, totalEvents, hasMore, onLoadMore }) => {
  const [expandedRows, setExpandedRows] = useState(new Set());
  const { getEventsTableColumns } = usePreferences();
  const columnConfig = getEventsTableColumns();
  const styles = TABLE_STYLES;

  const toggleRow = (eventId) => {
    const newExpanded = new Set(expandedRows);
    if (newExpanded.has(eventId)) {
      newExpanded.delete(eventId);
    } else {
      newExpanded.add(eventId);
    }
    setExpandedRows(newExpanded);
  };

  // Format date time for display
  const formatDateTime = (dateString) => {
    if (!dateString) return '';
    try {
      const date = new Date(dateString);
      return date.toLocaleString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
        hour: 'numeric',
        minute: '2-digit',
        second: '2-digit',
        hour12: true
      });
    } catch {
      return '';
    }
  };

  // Get last event timestamp
  const getLastEventTimestamp = () => {
    if (!events || events.length === 0) return '';
    const lastEvent = events[events.length - 1];
    return formatDateTime(lastEvent.timestamp || lastEvent.created_at);
  };

  if (loading) {
    return (
      <div className="overflow-hidden rounded-lg" style={{ backgroundColor: styles.primaryBg, border: `1px solid ${styles.borderColor}` }}>
        <div className="flex flex-col items-center justify-center px-6 py-12">
          <LiquidFillLoader size={100} />
          <span className="mt-4 text-sm" style={{ color: styles.textSecondary, fontFamily: 'Raleway, sans-serif' }}>
            Loading events...
          </span>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="overflow-hidden rounded-lg" style={{ backgroundColor: styles.primaryBg, border: `1px solid ${styles.borderColor}` }}>
        <div className="px-6 py-8 text-center" style={{ color: '#ef4444', fontFamily: 'Raleway, sans-serif' }}>
          Error: {error}
        </div>
      </div>
    );
  }

  if (!events || events.length === 0) {
    return (
      <div className="overflow-hidden rounded-lg" style={{ backgroundColor: styles.primaryBg, border: `1px solid ${styles.borderColor}` }}>
        <div className="px-6 py-8 text-center" style={{ color: styles.textSecondary, fontFamily: 'Raleway, sans-serif' }}>
          No events found.
        </div>
      </div>
    );
  }

  return (
    <>
      {/* Results count text above table */}
      {!loading && !error && events && events.length > 0 && (
        <div className="mb-2 px-1">
          <div className="text-xs text-[#475569]" style={{ fontFamily: 'Raleway, sans-serif' }}>
            Showing <span className="font-semibold">{events.length}</span> most recent results of{' '}
            {Number(totalEvents || 0).toLocaleString()} matches
          </div>
        </div>
      )}

      <div className="overflow-hidden rounded-lg" style={{ backgroundColor: styles.primaryBg, border: `1px solid ${styles.borderColor}` }}>
        {/* Table */}
        <div className="overflow-x-auto">
          <table className="w-full" style={{ fontFamily: 'Raleway, sans-serif' }}>
            <thead className="sticky top-0 z-10 bg-gray-100">
              <tr style={{ borderBottom: `2px solid ${styles.borderColor}` }}>
                {columnConfig.order
                  .filter(columnKey => columnConfig.enabled.includes(columnKey))
                  .map(columnKey => {
                    const columnDef = AVAILABLE_COLUMNS[columnKey];
                    if (!columnDef) return null;
                    
                    return (
                      <th
                        key={columnKey}
                        className={`px-6 py-3 text-xs font-semibold uppercase tracking-wider bg-gray-100 ${
                          columnKey === 'event_name' ? 'text-left' : 'text-center'
                        }`}
                        style={{ color: '#4b5563' }}
                      >
                        {columnDef.label}
                      </th>
                    );
                  })}
                {/* Expand/Collapse column */}
                <th className="px-6 py-3 text-right text-xs font-semibold uppercase tracking-wider bg-gray-100" style={{ color: '#4b5563' }}>
                  {/* Empty header for expand/collapse */}
                </th>
              </tr>
            </thead>
            <tbody>
              {events.map((event) => (
                <EventRow
                  key={event.id}
                  event={event}
                  isExpanded={expandedRows.has(event.id)}
                  onToggle={() => toggleRow(event.id)}
                  columns={columnConfig.order.filter(key => columnConfig.enabled.includes(key))}
                />
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Bottom "showing through" + Load More (outside table container) */}
      {hasMore && !loading && !error && events && events.length > 0 && (
        <div className="mt-3">
          <div className="mb-2 flex items-center justify-center">
            <div className="text-xs text-[#475569] text-center" style={{ fontFamily: 'Raleway, sans-serif' }}>
              Showing <span className="font-semibold">{events.length}</span> results through {getLastEventTimestamp()}
            </div>
          </div>
          <div className="flex items-center justify-center">
            <button
              onClick={onLoadMore}
              disabled={loadingMore}
              className="px-4 py-2 text-sm font-medium rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              style={{
                fontFamily: 'Raleway, sans-serif',
                backgroundColor: loadingMore ? styles.secondaryBg : styles.primaryBg,
                color: loadingMore ? styles.textSecondary : styles.textPrimary,
                border: `1px solid ${styles.borderColor}`
              }}
            >
              {loadingMore ? 'Loading...' : 'Load More'}
            </button>
          </div>
        </div>
      )}
    </>
  );
};

export default EventsTable;
